import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import LocationTracker from "../components/LocationTracker";
import { ArrowLeft, Compass, Navigation, Car, Footprints, RefreshCw } from "lucide-react";
import { openDirections } from "../lib/directions";
import { haversineMiles } from "../lib/geo";
import { toast } from "sonner";

export default function FindMe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [heading, setHeading] = useState(0);
  const compassRef = useRef(0);
  const [navigatingMode, setNavigatingMode] = useState(null); // null | "driving" | "walking"
  const lastNavLatLng = useRef(null); // {lat,lng} of contact when we last opened maps
  const [refreshable, setRefreshable] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/find/${id}`);
        setData(r.data);
        // Detect significant movement since last navigation launch
        if (navigatingMode && r.data?.them?.lat != null && lastNavLatLng.current) {
          const a = lastNavLatLng.current;
          const dMoved = haversineMiles(a, { lat: r.data.them.lat, lng: r.data.them.lng });
          if (dMoved >= 0.25) setRefreshable(true);
        }
      } catch {}
    };
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id, navigatingMode]);

  // Device orientation for compass (mobile only)
  useEffect(() => {
    const handler = (e) => {
      const h = e.webkitCompassHeading ?? (e.alpha != null ? 360 - e.alpha : 0);
      compassRef.current = h;
      setHeading(h);
    };
    window.addEventListener("deviceorientation", handler, true);
    return () => window.removeEventListener("deviceorientation", handler, true);
  }, []);

  const dist = data?.distance_miles;
  const bearing = data?.bearing_deg;
  const arrowRot = bearing != null ? bearing - heading : 0;

  const distLabel = (() => {
    if (dist == null) return "—";
    if (dist < 0.19) return `${Math.round(dist * 5280)}`;
    return dist.toFixed(2);
  })();
  const distUnit = dist != null && dist < 0.19 ? "ft" : "mi";

  const status = (() => {
    if (dist == null) return { text: "Acquiring signal…", color: "#FFCC00" };
    if (dist < 0.02) return { text: "Right next to you", color: "#34C759" };
    if (dist <= 0.5) return { text: "Very close", color: "#34C759" };
    if (dist <= 3) return { text: "Within proximity", color: "#007AFF" };
    return { text: "Out of range", color: "#FF3B30" };
  })();

  return (
    <div className="relative min-h-screen overflow-hidden bg-zinc-950 text-zinc-50">
      <LocationTracker enabled onUpdate={()=>{}}/>
      <button
        data-testid="findme-back"
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-20 glass size-11 rounded-full grid place-items-center"
      >
        <ArrowLeft size={18}/>
      </button>

      <div className="absolute top-4 right-4 z-20 glass rounded-full px-3 py-2 flex items-center gap-2 text-xs">
        <Compass size={14} className="text-[#FFCC00]"/>
        <span className="font-mono">{Math.round(heading)}°</span>
      </div>

      <div className="absolute inset-0 grid place-items-center px-6">
        {/* Radar rings */}
        <div className="relative size-[min(80vw,520px)] aspect-square">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="absolute inset-0 m-auto rounded-full border border-white/8"
              style={{ width: `${i * 22}%`, height: `${i * 22}%` }}
            />
          ))}
          {/* sweeping beam */}
          <div
            className="absolute inset-0 m-auto rounded-full"
            style={{
              background: `conic-gradient(from 0deg, rgba(0,122,255,0.25), transparent 50%)`,
              animation: "spin 4s linear infinite",
              width: "88%", height: "88%",
            }}
          />
          {/* center avatar */}
          <div className="absolute inset-0 grid place-items-center">
            <div className="size-16 rounded-full bg-zinc-900 border-2 border-[#007AFF] grid place-items-center">
              <div className="size-2.5 rounded-full bg-[#007AFF] animate-pulse"/>
            </div>
          </div>
          {/* target arrow */}
          {bearing != null && (
            <div
              className="absolute inset-0 m-auto"
              style={{ width: "100%", height: "100%", transform: `rotate(${arrowRot}deg)`, transition: "transform 0.5s ease-out" }}
            >
              <div className="absolute left-1/2 -translate-x-1/2 top-2">
                <div className="size-10 rounded-full bg-[#FF3B30] border-2 border-white grid place-items-center text-white font-bold font-display">
                  {(data?.contact?.name || "?").slice(0, 1)}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="absolute bottom-16 left-0 right-0 px-6 text-center">
          <div className="label-eyebrow">Finding</div>
          <div className="font-display font-black text-2xl tracking-tight mt-1">
            {data?.contact?.name || "Contact"}
          </div>
          <div className="mt-6 flex items-baseline justify-center gap-2">
            <span data-testid="findme-distance" className="font-display font-black text-7xl tracking-tighter font-mono">{distLabel}</span>
            <span className="text-2xl text-zinc-500 font-mono">{distUnit}</span>
          </div>
          <div className="mt-2 font-bold text-sm" style={{ color: status.color }}>
            {status.text}
          </div>
          {bearing != null && (
            <div className="mt-1 text-xs text-zinc-500 font-mono">
              bearing {Math.round(bearing)}°
            </div>
          )}

          {data?.them?.lat != null && (
            <div className="mt-7 flex items-center justify-center gap-2">
              <button
                data-testid="navigate-drive-btn"
                onClick={async () => {
                  const dest = { lat: data.them.lat, lng: data.them.lng };
                  lastNavLatLng.current = dest;
                  setNavigatingMode("driving");
                  setRefreshable(false);
                  openDirections({ ...dest, label: data.contact?.name || "Contact", mode: "driving" });
                  try {
                    const r = await api.post("/eta/start", { contact_user_id: id, mode: "driving" });
                    toast.success(`ETA shared · ~${r.data.eta_minutes} min`);
                  } catch {}
                }}
                className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold px-5 py-3 rounded-full"
              >
                <Navigation size={16}/> Drive to {data.contact?.name?.split(" ")[0] || "them"}
              </button>
              <button
                data-testid="navigate-walk-btn"
                onClick={async () => {
                  const dest = { lat: data.them.lat, lng: data.them.lng };
                  lastNavLatLng.current = dest;
                  setNavigatingMode("walking");
                  setRefreshable(false);
                  openDirections({ ...dest, label: data.contact?.name || "Contact", mode: "walking" });
                  try {
                    const r = await api.post("/eta/start", { contact_user_id: id, mode: "walking" });
                    toast.success(`ETA shared · ~${r.data.eta_minutes} min walking`);
                  } catch {}
                }}
                className="size-12 rounded-full border border-white/15 hover:bg-white/5 grid place-items-center"
                title="Walk"
              >
                <Footprints size={16}/>
              </button>
            </div>
          )}

          {navigatingMode && refreshable && (
            <div className="mt-4">
              <button
                data-testid="refresh-route-btn"
                onClick={() => {
                  if (!data?.them?.lat) return;
                  const dest = { lat: data.them.lat, lng: data.them.lng };
                  lastNavLatLng.current = dest;
                  setRefreshable(false);
                  openDirections({ ...dest, label: data.contact?.name || "Contact", mode: navigatingMode });
                  toast.message("Re-routing with their latest position");
                }}
                className="inline-flex items-center gap-2 bg-[#FFCC00] text-zinc-950 font-bold px-4 py-2.5 rounded-full text-sm animate-pulse"
              >
                <RefreshCw size={14}/> {data.contact?.name?.split(" ")[0] || "They"} moved — refresh route
              </button>
            </div>
          )}

          {navigatingMode && !refreshable && (
            <div className="mt-3 text-[11px] text-zinc-500 font-mono">
              Tracking · auto-detects when they move 0.25+ mi
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
